import React, { useMemo, useState } from 'react';
import { SUB_ACTIVE_STATUSES, USER_AND_LICENSES_TEST_IDS } from '@localstack/constants';

import { Permission, ProductFeature, UserRole, CreateInvitationRequest, PlanFamily } from '@localstack/types';

import { Alert, Button, Link } from '@mui/material';

import {
  InvitationService,
  MembershipService,
  OrganizationService,
  OrganizationsService,
  SubscriptionService,
  useApiEffect,
  useApiGetter,
  useRoutes,
} from '@localstack/services';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { BasicPageLayout, ContainedCustomerLayout, PageGutter } from '~/layouts';

import { InviteMemberModal, MembersAndLicenses } from '~/components/MembersAndLicenses';

const PAGE_PROPS = { title: 'Users & Licenses' };

const subtitle = (
  <span>A license is required to be able to start LocalStack locally or create a CI Auth Token to run in CI.</span>
);

export const TeamMembers = (): React.ReactElement => {
  const { can, hasFeature } = useAuthProvider();
  const { goto } = useRoutes();

  const [showInviteForm, setShowInviteForm] = useState(false);

  const hasScim = hasFeature(ProductFeature.FEATURE_SCIM);

  const { data: organization } = useApiGetter(OrganizationService, 'getOrganization', [], { enable: hasScim });

  const activeScimIdpName = useMemo(() => {
    if (!hasScim) return null;
    const enabledScimEntry = Object.entries(organization?.scim_settings ?? {}).find(
      ([, settings]) => settings?.is_enabled,
    );
    return enabledScimEntry?.[0] ?? null;
  }, [hasScim, organization?.scim_settings]);

  const { data: subscriptions, isLoading: areSubscriptionsLoading } = useApiGetter(
    SubscriptionService,
    'listSubscriptions',
    [],
    { defaultValue: [] },
  );

  const { data: members, isLoading: isMembersLoading } = useApiGetter(MembershipService, 'listMembers', [], {
    defaultValue: [],
  });

  const {
    data: invitations,
    isLoading: isInvitationsLoading,
    mutate: mutateInvitations,
  } = useApiGetter(InvitationService, 'listInvitations', [], { defaultValue: [] });

  const { data: licenseAssignments, isLoading: areLicenseAssignmentsLoading } = useApiGetter(
    OrganizationsService,
    'listLicenseAssignments',
    ['my'],
    { defaultValue: [] },
  );

  const activeSubscriptions = useMemo(
    () =>
      Object.fromEntries(Object.entries(subscriptions).filter(([_, sub]) => SUB_ACTIVE_STATUSES.includes(sub.status))),
    [Object.keys(subscriptions)],
  );

  const disallowInvites = useMemo(
    () =>
      Object.values(activeSubscriptions).length === 1 &&
      Object.values(activeSubscriptions)[0]?.plan.family === PlanFamily.FREEMIUM_PLANS,
    [activeSubscriptions],
  );

  const { setLicenseAssignmentsForUser } = useApiEffect(OrganizationsService, ['setLicenseAssignmentsForUser'], {
    revalidate: ['preflightSelfAssignLicense', 'listLicenseAssignmentsForUser', 'listLicenseAssignments'],
    revalidateOtherClient: { client: [SubscriptionService], methods: ['getAggregatedSubscriptionInfo'] },
  });
  const { updateMembership } = useApiEffect(MembershipService, ['updateMembership']);
  const { resendInvitation } = useApiEffect(InvitationService, ['resendInvitation']);

  const { deleteMember } = useApiEffect(MembershipService, ['deleteMember'], {
    revalidateOtherClient: { client: [OrganizationService], methods: ['getOrganizationStats'] },
  });

  const { createInvitation, deleteInvitation } = useApiEffect(
    InvitationService,
    ['createInvitation', 'deleteInvitation'],
    { revalidateOtherClient: { client: [OrganizationService], methods: ['getOrganizationStats'] } },
  );

  const sendInvitation = async (invitation: CreateInvitationRequest) => {
    const newInvitation = await createInvitation(invitation);
    await mutateInvitations((prev) => [newInvitation, ...(prev ?? [])], { revalidate: false });
  };

  if (!can(Permission.READ_MEMBERS) || !can(Permission.READ_SUBSCRIPTIONS)) {
    return (
      <ContainedCustomerLayout {...PAGE_PROPS}>
        <Alert severity="error">
          You do not have the necessary permissions to view or manage users in this workspace. Reach out to your
          workspace admin to review permissions for your account.
        </Alert>
      </ContainedCustomerLayout>
    );
  }

  return (
    <BasicPageLayout title="Users & Licenses" subtitle={subtitle} variant="full">
      {showInviteForm && (
        <InviteMemberModal
          disallowInvites={disallowInvites}
          onConfirm={sendInvitation}
          onClose={() => setShowInviteForm(false)}
        />
      )}

      {activeScimIdpName && (
        <PageGutter variant="full">
          <Alert
            severity="info"
            sx={{
              marginTop: (theme) => theme.spacing(1.5),
              marginBottom: (theme) => theme.spacing(1.5),
              '& .MuiAlert-action': { marginRight: 0 },
            }}
            action={
              <Link underline="always" onClick={() => goto(AppRoute.SETTINGS_SSO)} sx={{ whiteSpace: 'nowrap' }}>
                Manage SCIM Configuration
              </Link>
            }
          >
            Some member settings are managed by <strong>{activeScimIdpName}</strong> through SCIM.
          </Alert>
        </PageGutter>
      )}

      <MembersAndLicenses
        subscriptions={subscriptions}
        areSubscriptionsLoading={areSubscriptionsLoading}
        licenseAssignments={licenseAssignments}
        areLicenseAssignmentsLoading={areLicenseAssignmentsLoading}
        members={members}
        invitations={invitations}
        isMembersLoading={isMembersLoading}
        isInvitationsLoading={isInvitationsLoading}
        canUpdateMembers={can(Permission.UPDATE_MEMBERS)}
        canDeleteMembers={can(Permission.DELETE_MEMBERS)}
        deleteMember={(userId: string) => deleteMember(userId)}
        deleteInvitation={(invitationId: string) => deleteInvitation(invitationId)}
        updateMembershipRole={(userId: string, role: UserRole) => updateMembership(userId, { role })}
        updateMembershipPermissions={(userId: string, permissions: string[]) =>
          updateMembership(userId, { permissions })
        }
        resendInvitation={(invitationId: string) => resendInvitation(invitationId)}
        setLicenseAssignmentsForUser={(userId: string, subscriptionIds: string[]) =>
          setLicenseAssignmentsForUser('my', userId, {
            subscription_ids: subscriptionIds,
          })
        }
        scimIdpName={activeScimIdpName}
        spaceSearchBar
      >
        <Button
          size="small"
          color="primary"
          variant="contained"
          data-testId={USER_AND_LICENSES_TEST_IDS.INVITE_USER_BUTTON}
          disableElevation
          disabled={!can(Permission.CREATE_MEMBERS)}
          onClick={() => setShowInviteForm(true)}
        >
          Invite Members
        </Button>
      </MembersAndLicenses>
    </BasicPageLayout>
  );
};
